import React, {useEffect} from "react"
import {useLocation} from "react-router-dom"
import {useToggle} from "react-use"
import classNames from "classnames"
import {
	useAccessToken,
	useIsBankUser,
	useIsOrgUnitPilot,
	useIsOrgUser,
	useIsPartnerUser,
	useIsManagedSolutionsBankingUser,
	useIsUnitUser,
} from "../../services/auth"
import {UnitUserOnly} from "../../containers/PermissionedUser/PermissionedUser"
import useHtmlClassname from "../../hooks/useHtmlClassname"
import {IcomoonIconName} from "../Icon/icons"
import Icon from "../Icon/Icon"
import {hasPermission} from "../../services/permission"
import {getCurrentEnvironmentName} from "../../utilities/environment"
import {NavLink} from "../NavLink/NavLink"
import {useOrgBankingSetup} from "../../services/orgBankingSetup"
import {OrgAccountsSidebarItem} from "./OrgAccountsSidebarItem"
import {AccountsSidebarItem} from "./AccountsSidebarItem"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {findManagedSolutionsAccounts} from "../../resources/managedSolutions"

interface SidebarItemProps {
	label: string
	link: string
	icon: IcomoonIconName
	isExactRoute?: boolean
}

function SidebarDesktopToggleIcon({isExpanded, toggle}: {isExpanded: boolean; toggle: () => void}) {
	return (
		<div
			className={classNames("sidebar-toggle-icon sidebar-desktop-toggle-icon", isExpanded && "expanded")}
			onClick={toggle}
		>
			<Icon
				size={13}
				icon={
					isExpanded
						? "interface-arrows-button-left--interface-essential"
						: "interface-arrows-button-right--interface-essential"
				}
			/>
		</div>
	)
}

function SidebarMobileToggleIcon({isExpanded, toggle}: {isExpanded: boolean; toggle: () => void}) {
	return (
		<div
			className={classNames("sidebar-toggle-icon sidebar-mobile-toggle-icon", isExpanded && "expanded")}
			onClick={toggle}
		>
			<Icon
				size={12}
				icon={
					isExpanded
						? "interface-arrows-button-left--interface-essential"
						: "interface-arrows-button-right--interface-essential"
				}
			/>
		</div>
	)
}

function SidebarItem({label, link, icon, isExactRoute = false}: SidebarItemProps) {
	return (
		<li>
			<NavLink className="has-icon" activeClassName="is-active" to={link} end={isExactRoute}>
				<div className="border-effect"></div>
				<span className="icon">
					<Icon size={16} icon={icon} />
				</span>
				<span className="nav-link-label">{label}</span>
			</NavLink>
		</li>
	)
}

export default function Sidebar() {
	const location = useLocation()
	const [isAsideDesktopExpanded, toggleAsideDesktop] = useToggle(true)
	const [isAsideMobileExpanded, toggleAsideMobile] = useToggle(false)
	const {setup} = useOrgBankingSetup()
	const accessToken = useAccessToken()
	const operationalAccounts = useAsyncResult(
		() => findManagedSolutionsAccounts(accessToken, 0, 100, undefined, {purpose: "operational_account"}),
		[accessToken]
	)
	useHtmlClassname(
		() =>
			classNames(
				"has-aside-left has-aside-mobile-transition",
				isAsideDesktopExpanded && "has-aside-expanded",
				isAsideMobileExpanded && "has-aside-mobile-expanded"
			),
		[isAsideMobileExpanded, isAsideDesktopExpanded]
	)

	const sidebarMenuClassname = classNames(
		"sidebar",
		(isAsideDesktopExpanded || isAsideMobileExpanded) && "is-expanded",
		isAsideMobileExpanded && "is-mobile-expanded"
	)
	const isUnitUser = useIsUnitUser()
	const isBankUser = useIsBankUser()
	const canViewIncomingRtp = isUnitUser
	const isPartnerUser = useIsPartnerUser()
	const isOrgUser = useIsOrgUser()
	const isOrgUnitPilot = useIsOrgUnitPilot()
	const isManagedSolutionsBankingUser = useIsManagedSolutionsBankingUser()
	const env = getCurrentEnvironmentName()

	useEffect(() => {
		const isAssignmentsPage = location.pathname.includes("/assignments")

		toggleAsideMobile(false)

		if (isAssignmentsPage) {
			toggleAsideDesktop(false)
		}
	}, [location])

	const sidebarItems = (
		<>
			{!isPartnerUser && <SidebarItem label="Home" link="/" icon="home-button" isExactRoute />}
			{setup === "ReadyToLaunchBanking" || isManagedSolutionsBankingUser ? (
				<>
					<SidebarItem label="Branding" link="/managed-solutions/branding" icon="Desktop-Edit-brand-theme" />
					<OrgAccountsSidebarItem
						label="Org Accounts"
						icon="bank-account--business-products"
						operationalAccounts={operationalAccounts}
					/>
					<li className="sidebar-separator" />
					{hasPermission("rtl.user", "get") && (
						<SidebarItem label="Users" link="/managed-solutions/users" icon="user-group" />
					)}
					{hasPermission("rtl.application", "get") && (
						<>
							<SidebarItem
								label="Application Forms"
								link="/managed-solutions/application-forms"
								icon="common-file-text---files-&-folders"
							/>
							<SidebarItem label="Applications" link="/managed-solutions/applications" icon="user-id-card" />
						</>
					)}
					<SidebarItem label="Customers" link="/managed-solutions/customers" icon="user-multiple-new" />
					<AccountsSidebarItem
						label="Accounts"
						link="/managed-solutions/accounts"
						icon="bank-account--business-products"
						operationalAccounts={operationalAccounts}
					/>
					<SidebarItem
						label="Suggested Actions"
						link="/managed-solutions/intents"
						icon="money-note-1--business-products"
					/>
					{hasPermission("bill", "get") && (
						<SidebarItem label="Bills" link="/bills" icon="billing-account--business-products" />
					)}
				</>
			) : (
				<>
					{/* Application Forms link hidden - route still accessible at /application-forms */}
					{hasPermission("application", "get") && (
						<SidebarItem label="Applications" link="/applications" icon="user-id-card" />
					)}
					{hasPermission("customer", "get") && (
						<SidebarItem label="Customers" link="/customers" icon="user-multiple-new" />
					)}
					{isUnitUser ? (
						<SidebarItem label="Deposit Products" link="/deposit-products" icon="list-bullet-point-square" />
					) : null}
					{isUnitUser ? (
						<SidebarItem label="Wallets Terms" link="/wallet-terms" icon="list-bullet-point-square" />
					) : null}
					{isUnitUser ? <SidebarItem label="Bins" link="/bins" icon="credit-card-edit--business-products" /> : null}
					{hasPermission("account", "get") && (
						<SidebarItem label="Accounts" link="/accounts" icon="bank-account--business-products" />
					)}
					{hasPermission("card", "get") && <SidebarItem label="Cards" link="/cards" icon="credit-card" />}
					{hasPermission("org", "get") && (
						<SidebarItem label="Orgs" link="/orgs" icon="real-estate-building-5--building-construction" />
					)}
					{hasPermission("bank", "get") && isUnitUser && (
						<SidebarItem label="Partner Banks" link="/banks" icon="bank-branch-client" />
					)}
					{hasPermission("payment", "get") && (
						<SidebarItem label="Payments" link="/payments" icon="money-note-1--business-products" />
					)}

					{hasPermission("receivedPayment", "get") && (
						<SidebarItem label="Received Payments" link="/received-payments" icon="payment-box-incoming" />
					)}
					{hasPermission("checkPayment", "get") && (
						<SidebarItem label="Check Payments" link="/check-payments" icon="money-note-1--business-products" />
					)}
					{hasPermission("bill", "get") && (
						<SidebarItem label="Bills" link="/bills" icon="billing-account--business-products" />
					)}
					{hasPermission("checkDeposit", "get") ? (
						<SidebarItem label="Check Deposits" link="/check-deposits" icon="check-write--business-products" />
					) : null}
					{hasPermission("dispute", "get") ? (
						<SidebarItem label="Disputes" link="/disputes" icon="law-hammer--business-products" />
					) : null}
					{isOrgUser && isOrgUnitPilot ? (
						<SidebarItem label="Pilot Overview" link="/pilot-overview" icon="astronomy-rocket-1--school-science" />
					) : null}
					{isUnitUser ? <SidebarItem label="Card Designs" link="/card-designs" icon="credit-card-setting" /> : null}
					{isBankUser && <SidebarItem label="Incoming Wires" link="/incoming-wires" icon="payment-box-incoming" />}
					{isBankUser && (
						<SidebarItem label="Incoming ACH" link="/incoming-achs" icon="money-transfer--business-products" />
					)}
					{isBankUser && (
						<SidebarItem label="Incoming Checks" link="/incoming-checks" icon="check-write--business-products" />
					)}
					{!isUnitUser && hasPermission("depositProduct", "get") ? (
						<SidebarItem label="Deposit Products" link="/deposit-products" icon="list-bullet-point-square" />
					) : null}
					{!isUnitUser && hasPermission("walletTerms", "get") ? (
						<SidebarItem label="Wallets Terms" link="/wallet-terms" icon="list-bullet-point-square" />
					) : null}
					{isBankUser && (
						<SidebarItem
							label="Agreements"
							link="/org-bank-agreements"
							icon="programming-language-code--programing-apps-websites"
						/>
					)}
				</>
			)}
		</>
	)

	const riskAndComplianceSidebarItems = (
		<>
			<SidebarItem label="Adjustments" link="/adjustments" icon="cypto-currency-exchange-rate--business-products" />
			<SidebarItem label="Batch Accounts" link="/batch-accounts" icon="module-three--interface-essential" />
			<SidebarItem label="Linked Accounts" link="/linked-accounts" icon="Linked-accounts" />
			<SidebarItem label="Incoming ACH" link="/incoming-achs" icon="money-transfer--business-products" />
			<SidebarItem label="Incoming Wires" link="/incoming-wires" icon="payment-box-incoming" />
			{canViewIncomingRtp && <SidebarItem label="Incoming RTP" link="/incoming-rtp" icon="payment-box-incoming" />}
			<SidebarItem label="Incoming Checks" link="/incoming-checks" icon="check-write--business-products" />
			<SidebarItem label="Interest Settings" link="/interest-settings" icon="wayfinding-money-pass--travel" />
			<SidebarItem
				label="Transaction Monitoring"
				link="/transaction-monitoring"
				icon="electronics-resistor--computers-devices-electronics"
			/>
			<SidebarItem
				label="Agreements"
				link="/org-bank-agreements"
				icon="programming-language-code--programing-apps-websites"
			/>
		</>
	)

	return (
		<aside className={classNames(sidebarMenuClassname, env)}>
			<div className="sidebar-top-area">
				<SidebarDesktopToggleIcon isExpanded={isAsideDesktopExpanded} toggle={toggleAsideDesktop} />
				<SidebarMobileToggleIcon isExpanded={isAsideMobileExpanded} toggle={toggleAsideMobile} />
			</div>

			<div className="sidebar-menu-wrapper">
				<ul>{sidebarItems}</ul>

				<UnitUserOnly>
					<p className="subsection-header">Risk & Compliance</p>
					<ul>{riskAndComplianceSidebarItems}</ul>
				</UnitUserOnly>
			</div>
		</aside>
	)
}
